import {FilterListIcon} from '@/_application/icons/filter-list';
import {useProductListView} from '@/_application/modules/products/provider/product-list-view.provider';
import {Menu, Transition} from '@headlessui/react';
import {SortEnum, SortFields} from '__generated__/__types__';
import {useTranslation} from 'next-i18next';
import cn from 'classnames';
import {Fragment} from 'react';
import CheckIcon from '@/_application/icons/check-icon';
import {SortInput} from '@/_application/modules/products/hooks/use-product-filter-actions';
import {disabledSortFields} from '@/framework/static/sort-fields';

interface IProps {
    sortFields: SortFields;
    onSortChange: (sortInput: SortInput) => void;
}

function ProductSort({sortFields, onSortChange}: Readonly<IProps>) {
    const {t} = useTranslation();
    const {sort} = useProductListView();

    return (
        <Menu as="div" className="relative z-11">
            <div>
                <Menu.Button className="flex items-center gap-1 pl-20 font-bold text-primary">
                    <FilterListIcon />
                    {t('text-sort-by')}
                </Menu.Button>
            </div>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95">
                <Menu.Items className="absolute right-0 mt-1 w-full origin-top-right rounded-[10px] bg-white shadow-[rgba(0,0,0,0.24)_0px_3px_8px] focus:outline-none">
                    <div className="px-1 py-1">
                        {sortFields?.options
                            ?.filter(option => !disabledSortFields.includes(option?.value ?? ''))
                            .map(option => {
                                const optionValue = option?.value as string;
                                const direction =
                                    (sort && (sort as {[p: string]: SortEnum})[optionValue]) ?? SortEnum.Asc;
                                const sortItemClass = cn(
                                    'flex items-center justify-between mt-1 px-4 gap-2 py-2 hover:drop-shadow-lg w-full bg-white text-primary text-left rounded-[10px]',
                                );

                                return (
                                    <div key={`${option?.value}-${SortEnum.Asc}-${SortEnum.Desc}`}>
                                        <Menu.Item key={`${option?.value}-${SortEnum.Asc}`}>
                                            <button
                                                aria-label="Tùy chọn sắp xếp"
                                                className={sortItemClass}
                                                onClick={() =>
                                                    onSortChange({sortKey: optionValue, direction: SortEnum.Asc})
                                                }>
                                                {`${t(option?.label ?? '')} ${option?.value === 'price' ? t('text-ascending') : ''}`}
                                                {Object.keys(sort ?? {}).includes(optionValue) &&
                                                    direction === SortEnum.Asc && (
                                                        <CheckIcon className="text-green-400" />
                                                    )}
                                            </button>
                                        </Menu.Item>
                                        {optionValue === 'price' && (
                                            <Menu.Item key={`${option?.value}-${SortEnum.Desc}`}>
                                                <button
                                                    aria-label="Tùy chọn sắp xếp"
                                                    className={sortItemClass}
                                                    onClick={() =>
                                                        onSortChange({sortKey: optionValue, direction: SortEnum.Desc})
                                                    }>
                                                    {`${option?.label} ${t('text-descending')}`}
                                                    {Object.keys(sort ?? {}).includes(optionValue) &&
                                                        direction === SortEnum.Desc && (
                                                            <CheckIcon className="text-green-400" />
                                                        )}
                                                </button>
                                            </Menu.Item>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                </Menu.Items>
            </Transition>
        </Menu>
    );
}

export default ProductSort;
